/**
 * Deriv API v2 Authentication Service for SmartTrader
 * Handles PKCE OAuth2 flow, token exchange, account fetching, and OTP-based WebSocket auth.
 * Plain JS version — no TypeScript, no imports from React ecosystem.
 * Uses getDomainConfigSync() from dynamic-config for per-site appIdV2.
 */

const dynamicConfig = require('../config/dynamic-config');

const AUTH_BASE = {
    production: 'https://auth.deriv.com/oauth2/',
    staging   : 'https://staging-auth.deriv.com/oauth2/',
};

const DERIVWS_BASE = {
    production: 'https://api.derivws.com/trading/v1/',
    staging   : 'https://staging-api.derivws.com/trading/v1/',
};

const TOKEN_PROXY = '/api/deriv-oauth-token';

const STORAGE_KEYS = {
    AUTH_INFO    : 'v2_auth_info',
    ACCOUNTS     : 'v2_accounts',
    WS_URL       : 'v2_ws_url',
    CLIENT_ID    : 'v2_client_id',
    REDIRECT_URI : 'v2_redirect_uri',
    PKCE_VERIFIER: 'v2_pkce_verifier',
    OAUTH_STATE  : 'v2_oauth_state',
};

// ─── Session storage routing ──────────────────────────────────────────────────
// Durable session data (tokens + account list, incl. the refresh_token) lives in
// localStorage so a v2 login survives a tab close AND mobile-OS memory eviction —
// the browser reclaiming a backgrounded tab after the user leaves for a while.
// Storing the refresh_token durably lets us mint a fresh access token on return
// instead of forcing a re-login every ~hour (the sessionStorage-only approach
// logged mobile users out when the OS evicted the tab).
//
// Only the short-lived OAuth handshake values (PKCE verifier, CSRF state, the
// per-login client_id / redirect_uri, and the one-shot OTP WS URL) stay in
// sessionStorage — they're consumed during the auth round-trip and must not
// outlive it.
const DURABLE_KEYS = new Set([
    STORAGE_KEYS.AUTH_INFO,
    STORAGE_KEYS.ACCOUNTS,
]);

function ssGet(key) {
    if (typeof window === 'undefined') return null;
    if (DURABLE_KEYS.has(key)) {
        const durable = localStorage.getItem(key);
        if (durable !== null) return durable;
        // One-time migration: a session created on the old sessionStorage-only
        // build. Promote it to localStorage so it survives from now on.
        const legacy = sessionStorage.getItem(key);
        if (legacy !== null) {
            try {
                localStorage.setItem(key, legacy);
                sessionStorage.removeItem(key);
            } catch {
                /* storage full / disabled — return the value anyway */
            }
            return legacy;
        }
        return null;
    }
    return sessionStorage.getItem(key);
}

function ssSet(key, value) {
    if (typeof window === 'undefined') return;
    (DURABLE_KEYS.has(key) ? localStorage : sessionStorage).setItem(key, value);
}

function ssRemove(key) {
    if (typeof window === 'undefined') return;
    // Remove from both stores so logout and migration never leave a stale copy.
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
}

function getClientId() {
    const config = dynamicConfig.getDomainConfigSync();
    if (config && config.appIdV2) return config.appIdV2;
    return process.env.DERIV_V2_CLIENT_ID || '';
}

function getSessionClientId() {
    try {
        const authInfo = getAuthInfo();
        if (authInfo && authInfo.client_id) return authInfo.client_id;
        return ssGet(STORAGE_KEYS.CLIENT_ID) || '';
    } catch {
        return '';
    }
}

function isProductionEnv() {
    const hostname = window.location.hostname;
    return hostname !== 'localhost' && !hostname.includes('staging') && !hostname.includes('qa');
}

function getAuthBase() {
    return isProductionEnv() ? AUTH_BASE.production : AUTH_BASE.staging;
}

function getApiBase() {
    return isProductionEnv() ? DERIVWS_BASE.production : DERIVWS_BASE.staging;
}

function getOAuthRouterBase() {
    if (typeof process !== 'undefined' && process.env && process.env.DERIV_OAUTH_ROUTER_BASE) {
        return process.env.DERIV_OAUTH_ROUTER_BASE.replace(/\/+$/, '');
    }
    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3000';
    }
    return 'https://dforge.site';
}

function getRouterRedirectUri() {
    return `${getOAuthRouterBase()}/oauth-router`;
}

function getInitUrl() {
    return `${getOAuthRouterBase()}/oauth-router/init`;
}

function useRouterFlow() {
    const config = dynamicConfig.getDomainConfigSync();
    // Production: per-site appIdV2 from Appwrite triggers the router flow.
    // The shared dforge.site/oauth-router is the registered redirect_uri for
    // every site's v2 tier app, so this is the only path that actually works.
    if (config && (config.authMode === 'v2_oauth' || config.appIdV2)) return true;
    // Dev-only: localhost / ngrok / devtunnels with build-time env vars set.
    // Never enable router flow in production based on env alone — appIdV2 is
    // per-site (tier-specific) and must come from Appwrite.
    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        const isDevHost = h === 'localhost' || h === '127.0.0.1' || h.includes('ngrok') || h.includes('devtunnels');
        if (isDevHost && typeof process !== 'undefined' && process.env && process.env.DERIV_V2_CLIENT_ID && process.env.DERIV_OAUTH_ROUTER_BASE) {
            return true;
        }
    }
    return false;
}

// ─── PKCE Helpers ────────────────────────────────────────────────────────────

function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const base64 = btoa(String.fromCharCode.apply(null, array));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const base64 = btoa(String.fromCharCode.apply(null, hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Login URL ───────────────────────────────────────────────────────────────

async function buildLoginURL() {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const nonce = generateState();
    const clientId = getClientId();
    const routerFlow = useRouterFlow();
    const redirectUri = routerFlow
        ? getRouterRedirectUri()
        : `${window.location.origin}/callback`;

    ssSet(STORAGE_KEYS.PKCE_VERIFIER, verifier);
    ssSet(STORAGE_KEYS.OAUTH_STATE, nonce);
    ssSet(STORAGE_KEYS.CLIENT_ID, clientId);
    ssSet(STORAGE_KEYS.REDIRECT_URI, redirectUri);

    if (routerFlow) {
        const initUrl = new URL(getInitUrl());
        initUrl.searchParams.set('origin', window.location.origin);
        initUrl.searchParams.set('client_id', clientId);
        initUrl.searchParams.set('code_challenge', challenge);
        initUrl.searchParams.set('nonce', nonce);
        // SmartTrader only trades. Requesting the shared router's broader
        // cashier scopes makes Deriv reject apps without payment capability.
        initUrl.searchParams.set('scope', 'trade');
        return initUrl.toString();
    }

    // per-site fallback (no appIdV2 configured)
    const base = getAuthBase();
    const url = new URL(`${base}auth`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('scope', 'trade');
    url.searchParams.set('state', nonce);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    return url.toString();
}

// ─── Token Exchange ──────────────────────────────────────────────────────────

async function exchangeCodeForToken(code, state) {
    const storedState = ssGet(STORAGE_KEYS.OAUTH_STATE);
    if (!storedState || storedState !== state) {
        // eslint-disable-next-line no-console
        console.error('[DerivV2Auth] OAuth state mismatch — possible CSRF');
        ssRemove(STORAGE_KEYS.OAUTH_STATE);
        return null;
    }

    const verifier = ssGet(STORAGE_KEYS.PKCE_VERIFIER);
    if (!verifier) {
        // eslint-disable-next-line no-console
        console.error('[DerivV2Auth] PKCE verifier not found');
        return null;
    }

    const clientId = ssGet(STORAGE_KEYS.CLIENT_ID) || getClientId();
    const redirectUri = ssGet(STORAGE_KEYS.REDIRECT_URI) ||
        (useRouterFlow() ? getRouterRedirectUri() : `${window.location.origin}/callback`);

    try {
        const bodyParams = new URLSearchParams({
            grant_type   : 'authorization_code',
            code,
            client_id    : clientId,
            redirect_uri : redirectUri,
            code_verifier: verifier,
        });

        const resp = await fetch(TOKEN_PROXY, {
            method     : 'POST',
            credentials: 'include',
            headers    : { 'Content-Type': 'application/x-www-form-urlencoded' },
            body       : bodyParams.toString(),
        });

        if (!resp.ok) {
            // eslint-disable-next-line no-console
            console.error('[DerivV2Auth] Token exchange HTTP error:', resp.status);
            return null;
        }

        const data = await resp.json();
        if (data.error || !data.access_token) {
            // eslint-disable-next-line no-console
            console.error('[DerivV2Auth] Token exchange failed:', data.error);
            return null;
        }

        ssRemove(STORAGE_KEYS.PKCE_VERIFIER);
        ssRemove(STORAGE_KEYS.OAUTH_STATE);

        const authInfo = {
            access_token: data.access_token,
            token_type  : data.token_type || 'bearer',
            expires_in  : data.expires_in || 3600,
            expires_at  : Date.now() + (data.expires_in || 3600) * 1000 - 60000,
            client_id   : clientId,
        };
        if (data.refresh_token) authInfo.refresh_token = data.refresh_token;

        ssSet(STORAGE_KEYS.AUTH_INFO, JSON.stringify(authInfo));
        ssRemove(STORAGE_KEYS.REDIRECT_URI);
        return authInfo;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[DerivV2Auth] Token exchange error:', err);
        return null;
    }
}

// ─── Auth Info ───────────────────────────────────────────────────────────────

function getAuthInfo() {
    try {
        const str = ssGet(STORAGE_KEYS.AUTH_INFO);
        if (!str) return null;
        const info = JSON.parse(str);
        if (Date.now() >= info.expires_at) {
            // Access token expired. If a refresh_token is present the session is
            // still recoverable (getValidAccessToken refreshes it), so do NOT
            // delete it here — only drop a truly dead (refreshless) token.
            // Otherwise the first caller after a mobile-eviction return wipes the
            // refresh_token before it can mint a fresh access token.
            if (!info.refresh_token) {
                ssRemove(STORAGE_KEYS.AUTH_INFO);
                return null;
            }
        }
        return info;
    } catch {
        return null;
    }
}

function isAuthenticated() {
    return getAuthInfo() !== null;
}

// ─── Refresh Flow ────────────────────────────────────────────────────────────
// Mirrors the multi_platform_sites pattern. Access tokens are short-lived
// (~1h); without a refresh path we'd force users back through OAuth as soon as
// they idle past the expiry. With a refresh_token in auth_info we can mint a
// fresh access_token transparently.

async function refreshAccessToken() {
    try {
        const str = ssGet(STORAGE_KEYS.AUTH_INFO);
        if (!str) return null;
        const authInfo = JSON.parse(str);
        if (!authInfo || !authInfo.refresh_token) return null;

        const clientId = authInfo.client_id || getSessionClientId() || getClientId();
        const body = new URLSearchParams({
            grant_type   : 'refresh_token',
            refresh_token: authInfo.refresh_token,
            client_id    : clientId,
        });
        const resp = await fetch(TOKEN_PROXY, {
            method     : 'POST',
            credentials: 'include',
            headers    : { 'Content-Type': 'application/x-www-form-urlencoded' },
            body       : body.toString(),
        });
        if (!resp.ok) return null;
        const data = await resp.json();
        if (data.error || !data.access_token) return null;

        const next = {
            access_token: data.access_token,
            token_type  : data.token_type || 'bearer',
            expires_in  : data.expires_in || 3600,
            expires_at  : Date.now() + (data.expires_in || 3600) * 1000 - 60000,
            client_id   : clientId,
        };
        if (data.refresh_token) next.refresh_token = data.refresh_token;
        else if (authInfo.refresh_token) next.refresh_token = authInfo.refresh_token;

        ssSet(STORAGE_KEYS.AUTH_INFO, JSON.stringify(next));
        return next.access_token;
    } catch {
        return null;
    }
}

async function getValidAccessToken() {
    let authInfo = null;
    try {
        const str = ssGet(STORAGE_KEYS.AUTH_INFO);
        if (str) authInfo = JSON.parse(str);
    } catch {
        authInfo = null;
    }
    if (!authInfo) return null;
    if (Date.now() < authInfo.expires_at) return authInfo.access_token;
    return refreshAccessToken();
}

// ─── Accounts ────────────────────────────────────────────────────────────────

async function fetchAccounts(accessToken) {
    const clientId = getSessionClientId() || getClientId();
    const base = getApiBase();
    try {
        const resp = await fetch(`${base}options/accounts`, {
            headers: {
                Authorization : `Bearer ${accessToken}`,
                'Deriv-App-ID': clientId,
            },
        });
        if (!resp.ok) return [];
        const data = await resp.json();
        return data.data || [];
    } catch {
        return [];
    }
}

function storeAccounts(accounts) {
    ssSet(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts));
}

function getStoredAccounts() {
    try {
        const str = ssGet(STORAGE_KEYS.ACCOUNTS);
        return str ? JSON.parse(str) : null;
    } catch {
        return null;
    }
}

// ─── WebSocket OTP URL ───────────────────────────────────────────────────────

async function fetchWebSocketURL(accessToken, accountId, timeoutMs = 8000) {
    const clientId = getSessionClientId() || getClientId();
    const base = getApiBase();
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const resp = await fetch(`${base}options/accounts/${accountId}/otp`, {
            method : 'POST',
            signal : controller.signal,
            headers: {
                Authorization : `Bearer ${accessToken}`,
                'Deriv-App-ID': clientId,
            },
        });
        clearTimeout(timerId);
        if (!resp.ok) return null;
        const data = await resp.json();
        return (data && data.data && data.data.url) || null;
    } catch (err) {
        clearTimeout(timerId);
        if (err && err.name === 'AbortError') {
            // eslint-disable-next-line no-console
            console.warn('[DerivV2Auth] OTP fetch timed out');
        }
        return null;
    }
}

function storeWebSocketURL(url) {
    // Transient (one-shot OTP URL) — routes to sessionStorage via ssSet.
    ssSet(STORAGE_KEYS.WS_URL, url);
}

function getStoredWebSocketURL() {
    return ssGet(STORAGE_KEYS.WS_URL);
}

function clearStoredWebSocketURL() {
    ssRemove(STORAGE_KEYS.WS_URL);
}

/**
 * Resolve a FRESH authenticated WebSocket URL for the active v2 account.
 *
 * Deriv OTP-WS URLs are one-shot / short-lived — they're consumed by the
 * WebSocket handshake and cannot be cached across reconnects. Every WebSocket
 * open (including reconnects after a network blip) must call this to mint a
 * brand-new OTP. Mirrors the pattern in multi_platform_sites'
 * generateDerivApiInstance.
 */
async function getAuthenticatedWebSocketURL() {
    const accessToken = await getValidAccessToken();
    if (!accessToken) return null;

    let accounts = getStoredAccounts();
    if (!accounts || accounts.length === 0) {
        accounts = await fetchAccounts(accessToken);
        if (accounts.length) storeAccounts(accounts);
    }
    if (!accounts || accounts.length === 0) return null;

    const activeId = (typeof window !== 'undefined' && window.localStorage)
        ? window.localStorage.getItem('active_loginid')
        : null;
    const target = (activeId && accounts.find(a => a.account_id === activeId)) || accounts[0];

    return fetchWebSocketURL(accessToken, target.account_id);
}

// ─── Full Auth Flow (called from callback) ───────────────────────────────────

async function completeAuthFlow(code, state) {
    const authInfo = await exchangeCodeForToken(code, state);
    if (!authInfo) {
        return { success: false, error: 'Token exchange failed. Please try again.' };
    }

    const accounts = await fetchAccounts(authInfo.access_token);
    if (!accounts.length) {
        return { success: false, error: 'No accounts found. Please sign up for a Deriv account.' };
    }

    storeAccounts(accounts);

    const activeAccount = accounts[0];
    localStorage.setItem('active_loginid', activeAccount.account_id);
    localStorage.setItem('account_type', activeAccount.account_type);
    localStorage.removeItem('is_api_token_login');

    // Do NOT pre-fetch / cache the OTP-WS URL here. Deriv OTPs are one-shot
    // and short-lived; caching them causes 1006 abnormal-close on the first
    // WebSocket open after the OTP is consumed. getSocketURL re-mints fresh
    // on every connect (and every reconnect) via getAuthenticatedWebSocketURL.
    clearStoredWebSocketURL();

    return { success: true, accounts };
}

// ─── Account Switching ────────────────────────────────────────────────────────

async function switchAccount(accountId) {
    const accounts = getStoredAccounts();
    const account = accounts && accounts.find(a => a.account_id === accountId);
    if (!account) return false;

    // Write the new active loginid + clear any cached OTP URL. We don't pre-fetch
    // a new OTP — the WebSocket that opens on the next page reload (or reconnect)
    // will mint one via getAuthenticatedWebSocketURL, which always reads the
    // current active_loginid.
    localStorage.setItem('active_loginid', accountId);
    localStorage.setItem('account_type', account.account_type);
    clearStoredWebSocketURL();
    return true;
}

// ─── Cleanup ─────────────────────────────────────────────────────────────────

function clearAll() {
    // ssRemove clears both localStorage and sessionStorage, so durable and
    // transient keys are all wiped regardless of where they landed.
    Object.values(STORAGE_KEYS).forEach(key => {
        ssRemove(key);
    });
    localStorage.removeItem('active_loginid');
    localStorage.removeItem('account_type');
    localStorage.removeItem('is_api_token_login');
}

module.exports = {
    buildLoginURL,
    completeAuthFlow,
    switchAccount,
    getAuthInfo,
    isAuthenticated,
    getStoredAccounts,
    getStoredWebSocketURL,
    storeWebSocketURL,
    clearStoredWebSocketURL,
    getAuthenticatedWebSocketURL,
    getValidAccessToken,
    refreshAccessToken,
    getSessionClientId,
    clearAll,
};
